import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { CheckCircle, Copy, ExternalLink, Home, Calendar, Hash } from 'lucide-react';
import { Navigation } from '../components/Navigation';
import { Footer } from '../components/Footer';
import { Button } from '../components/ui/Button';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { formatOrderNumber } from '../utils/orderNumber';
export function ThankYouPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [copied, setCopied] = useState(false);
  const [orderCopied, setOrderCopied] = useState(false);
  const name = searchParams.get('name') || 'חבר';
  const email = searchParams.get('email') || '';
  const orderNumber = searchParams.get('orderNumber') || '';
  // Coupon code for accommodation discount
  const COUPON_CODE = 'WATERTRIBE2026';
  const ACCOMMODATION_LINK = 'https://www.maagan.co.il'; // Replace with actual link
  const handleCopyCoupon = () => {
    navigator.clipboard.writeText(COUPON_CODE);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  const handleCopyOrderNumber = () => {
    navigator.clipboard.writeText(formatOrderNumber(orderNumber));
    setOrderCopied(true);
    setTimeout(() => setOrderCopied(false), 2000);
  };
  useEffect(() => {
    // Scroll to top
    window.scrollTo(0, 0);
  }, []);
  return <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white" dir="rtl" data-id="element-1166">
      <Navigation data-id="element-1167" />

      <div className="container mx-auto px-4 py-20" data-id="element-1168">
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.8
      }} className="max-w-3xl mx-auto" data-id="element-1169">
          {/* Success Icon */}
          <motion.div initial={{
          scale: 0
        }} animate={{
          scale: 1
        }} transition={{
          delay: 0.2,
          type: 'spring',
          stiffness: 200
        }} className="flex justify-center mb-8" data-id="element-1170">
            <div className="bg-green-100 rounded-full p-6" data-id="element-1171">
              <CheckCircle className="w-20 h-20 text-green-600" data-id="element-1172" />
            </div>
          </motion.div>

          {/* Thank You Message */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.4
        }} className="text-center mb-12" data-id="element-1173">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4" data-id="element-1174">
              תודה {name}! 🎉
            </h1>
            <p className="text-xl text-gray-600 leading-relaxed" data-id="element-1175">
              ההרשמה שלך ל-Water Tribe Fest 2026 התקבלה בהצלחה!
            </p>
            <p className="text-lg text-gray-500 mt-2" data-id="element-1176">
              קיבלת אישור למייל: <span className="font-semibold" data-id="element-1177">{email}</span>
            </p>
          </motion.div>

          {/* Order Number Card */}
          {orderNumber && <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.5
        }} className="bg-white rounded-2xl p-6 md:p-8 shadow-lg border-2 border-indigo-200 mb-8" data-id="element-1178">
              <div className="flex items-center gap-3 mb-4" data-id="element-1179">
                <div className="bg-indigo-100 rounded-full p-2" data-id="element-1180">
                  <Hash className="w-6 h-6 text-indigo-600" data-id="element-1181" />
                </div>
                <h2 className="text-xl md:text-2xl font-bold text-gray-900" data-id="element-1182">
                  מספר הזמנה
                </h2>
              </div>

              <p className="text-gray-600 mb-4" data-id="element-1183">
                זהו מספר ההזמנה הייחודי שלך. שמור אותו לצורך התכתבות ואימות
                בכניסה לאירוע.
              </p>

              <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-xl p-6 border-2 border-indigo-100" data-id="element-1184">
                <div className="flex items-center justify-between gap-4" data-id="element-1185">
                  <div className="flex-1" data-id="element-1186">
                    <p className="text-sm text-gray-600 mb-2" data-id="element-1187">
                      מספר ההזמנה שלך:
                    </p>
                    <p className="text-3xl md:text-4xl font-black text-indigo-600 tracking-wider" data-id="element-1188">
                      {formatOrderNumber(orderNumber)}
                    </p>
                  </div>
                  <button onClick={handleCopyOrderNumber} className="bg-indigo-100 hover:bg-indigo-200 p-4 rounded-xl transition-colors shrink-0" data-id="element-1189">
                    {orderCopied ? <CheckCircle className="w-6 h-6 text-indigo-600" data-id="element-1190" /> : <Copy className="w-6 h-6 text-indigo-600" data-id="element-1191" />}
                  </button>
                </div>
              </div>
            </motion.div>}

          {/* Coupon Code Card */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.6
        }} className="bg-gradient-to-br from-[#0A5F8F] to-[#0D7AB8] rounded-3xl p-8 md:p-10 text-white mb-8 shadow-2xl" data-id="element-1192">
            <div className="flex items-center gap-3 mb-6" data-id="element-1193">
              <Calendar className="w-8 h-8" data-id="element-1194" />
              <h2 className="text-2xl md:text-3xl font-bold" data-id="element-1195">
                קוד קופון מיוחד!
              </h2>
            </div>

            <p className="text-blue-100 text-lg mb-6 leading-relaxed" data-id="element-1196">
              קיבלת קוד קופון להנחה על אירוח בכפר הנופש מעגן במהלך הפסטיבל
              (12-14 במרץ 2026)
            </p>

            {/* Coupon Code Display */}
            <div className="bg-white/20 backdrop-blur-md rounded-2xl p-6 border-2 border-white/30 mb-6" data-id="element-1197">
              <div className="flex items-center justify-between gap-4" data-id="element-1198">
                <div className="flex-1" data-id="element-1199">
                  <p className="text-sm text-blue-100 mb-2" data-id="element-1200">קוד הקופון שלך:</p>
                  <p className="text-3xl md:text-4xl font-black tracking-wider" data-id="element-1201">
                    {COUPON_CODE}
                  </p>
                </div>
                <button onClick={handleCopyCoupon} className="bg-white/20 hover:bg-white/30 p-4 rounded-xl transition-colors shrink-0" data-id="element-1202">
                  {copied ? <CheckCircle className="w-6 h-6" data-id="element-1203" /> : <Copy className="w-6 h-6" data-id="element-1204" />}
                </button>
              </div>
            </div>

            {/* Accommodation Link */}
            <a href={ACCOMMODATION_LINK} target="_blank" rel="noopener noreferrer" className="block" data-id="element-1205">
              <Button variant="outline" className="w-full border-2 border-white text-white hover:bg-white hover:text-[#0A5F8F] text-lg py-6" data-id="element-1206">
                <ExternalLink className="w-5 h-5 ml-2" data-id="element-1207" />
                הזמן אירוח בכפר הנופש מעגן
              </Button>
            </a>

            <p className="text-sm text-blue-100 text-center mt-4" data-id="element-1208">
              * השתמש בקוד הקופון בעת ההזמנה לקבלת ההנחה
            </p>
          </motion.div>

          {/* Next Steps */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.8
        }} className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100 mb-8" data-id="element-1209">
            <h3 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-1210">מה הלאה?</h3>
            <div className="space-y-4" data-id="element-1211">
              <div className="flex items-start gap-4" data-id="element-1212">
                <div className="bg-blue-100 rounded-full p-2 shrink-0" data-id="element-1213">
                  <CheckCircle className="w-5 h-5 text-[#0A5F8F]" data-id="element-1214" />
                </div>
                <div data-id="element-1215">
                  <h4 className="font-bold text-gray-900 mb-1" data-id="element-1216">אישור במייל</h4>
                  <p className="text-gray-600" data-id="element-1217">
                    קיבלת אישור הרשמה למייל עם כל הפרטים החשובים
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4" data-id="element-1218">
                <div className="bg-blue-100 rounded-full p-2 shrink-0" data-id="element-1219">
                  <CheckCircle className="w-5 h-5 text-[#0A5F8F]" data-id="element-1220" />
                </div>
                <div data-id="element-1221">
                  <h4 className="font-bold text-gray-900 mb-1" data-id="element-1222">
                    הצטרף לקבוצת הווטסאפ
                  </h4>
                  <p className="text-gray-600 mb-2" data-id="element-1223">
                    הצטרף לקבוצת הווטסאפ השקטה של הפסטיבל לעדכונים
                  </p>
                  <a href="https://chat.whatsapp.com/BTCYcMWElUsEX6Ds48mQFA" target="_blank" rel="noopener noreferrer" className="text-[#0A5F8F] hover:underline font-semibold inline-flex items-center gap-2" data-id="element-1224">
                    לחץ כאן להצטרפות
                    <ExternalLink className="w-4 h-4" data-id="element-1225" />
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4" data-id="element-1226">
                <div className="bg-blue-100 rounded-full p-2 shrink-0" data-id="element-1227">
                  <CheckCircle className="w-5 h-5 text-[#0A5F8F]" data-id="element-1228" />
                </div>
                <div data-id="element-1229">
                  <h4 className="font-bold text-gray-900 mb-1" data-id="element-1230">הזמן אירוח</h4>
                  <p className="text-gray-600" data-id="element-1231">
                    אל תשכח להזמין אירוח בכפר הנופש עם הקוד קופון המוזל
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4" data-id="element-1232">
                <div className="bg-blue-100 rounded-full p-2 shrink-0" data-id="element-1233">
                  <Calendar className="w-5 h-5 text-[#0A5F8F]" data-id="element-1234" />
                </div>
                <div data-id="element-1235">
                  <h4 className="font-bold text-gray-900 mb-1" data-id="element-1236">סמן ביומן</h4>
                  <p className="text-gray-600" data-id="element-1237">
                    12-14 במרץ 2026 | חוף מעגן עדן, כנרת
                  </p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Back to Home Button */}
          <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          delay: 1
        }} className="text-center" data-id="element-1238">
            <Button onClick={() => navigate('/water-tribe-fest')} variant="outline" className="inline-flex items-center gap-2" data-id="element-1239">
              <Home className="w-5 h-5" data-id="element-1240" />
              חזרה לדף הפסטיבל
            </Button>
          </motion.div>
        </motion.div>
      </div>

      <Footer data-id="element-1241" />
    </div>;
}